import { Button, Select, Space, Tag } from 'antd';
import * as Component from 'components';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { videoService } from 'service/api/videoService';
import { TableColumnInterface } from 'type/tableType';
import { VideoType } from 'type/videoType';
import { formatDateTime, formatDuration } from '../../../../shared/utils/timeFormat';

export interface VideoAssetType {
  id: number;
  name: string;
  image?: string;
  source: 'inhouse' | 'mediahold' | string;
  property?: string;
  status: 'processing' | 'completed' | 'failed' | string;
  created: string;
  updated: string;
  file_size?: number;
  duration?: number;
}

export default function Assets(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedList, setSelectedList] = useState<number[]>([]);

  // Filter states - initialized from URL params
  const [sourceFilter, setSourceFilter] = useState<string | undefined>(
    searchParams.get('ingest_source') || undefined
  );
  const [assignedFilter, setAssignedFilter] = useState<string | undefined>(
    searchParams.get('is_assigned') || undefined
  );
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    searchParams.get('status') || undefined
  );

  // Sync filter states with URL params when they change
  useEffect(() => {
    setSourceFilter(searchParams.get('ingest_source') || undefined);
    setAssignedFilter(searchParams.get('is_assigned') || undefined);
    setStatusFilter(searchParams.get('status') || undefined);
  }, [searchParams]);

  const fetchAssets = useCallback(
    async (searchParams: URLSearchParams): Promise<{ data: VideoType[]; count: number }> => {
      searchParams.set('search', searchParams.get('search') || '');

      // Add filter parameters if they exist
      if (searchParams.get('ingest_source')) {
        searchParams.set('ingest_source', searchParams.get('ingest_source') || '');
      }
      if (searchParams.get('is_assigned')) {
        searchParams.set('is_assigned', searchParams.get('is_assigned') || '');
      }
      if (searchParams.get('status')) {
        searchParams.set('status', searchParams.get('status') || '');
      }

      const videos = await videoService.fetchVideos(searchParams);

      return {
        data: videos.data,
        count: videos.count,
      };
    },
    []
  );

  // Filter change handlers
  const handleSourceFilterChange = (value: string | undefined) => {
    setSourceFilter(value);
    setSearchParams(prev => {
      if (value) {
        prev.set('ingest_source', value);
      } else {
        prev.delete('ingest_source');
      }
      prev.set('page', '1');
      return prev;
    });
  };

  const handleAssignedFilterChange = (value: string | undefined) => {
    setAssignedFilter(value);
    setSearchParams(prev => {
      if (value) {
        prev.set('is_assigned', value);
      } else {
        prev.delete('is_assigned');
      }
      prev.set('page', '1');
      return prev;
    });
  };

  const handleStatusFilterChange = (value: string | undefined) => {
    setStatusFilter(value);
    setSearchParams(prev => {
      if (value) {
        prev.set('status', value);
      } else {
        prev.delete('status');
      }
      prev.set('page', '1');
      return prev;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'COMPLETED':
        return 'green';
      case 'processing':
      case 'IN_PROGRESS':
        return 'blue';
      case 'failed':
      case 'FAILED':
        return 'red';
      default:
        return 'default';
    }
  };

  const columns: TableColumnInterface<VideoType>[] = [
    {
      title: 'Ingested DateTime',
      dataIndex: 'created',
      key: 'created',
      width: '10%',
      sorter: true as any,
      render: (_: string, record: VideoType) => (
        <span style={{ fontWeight: 500 }}>
          {record.created ? formatDateTime(record.created) : '-'}
        </span>
      ),
    },
    {
      title: 'Assigned to Episode',
      dataIndex: 'contents',
      key: 'contents',
      width: '10%',
      render: (_: string, record: VideoType) => (
        <a
          style={{ color: '#1890ff', fontWeight: 500, cursor: 'pointer' }}
          onClick={() => {
            if (record.contents && record.contents.length > 0 && record.contents[0]?.id) {
              navigate(`/video/detail/${record.contents[0].id}`);
            }
          }}
        >
          {record.contents && record.contents.length > 0 ? record.contents[0]?.title : '-'}
        </a>
      ),
    },
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      width: '60%',
      sorter: true as any,
      render: (_: string, record: VideoType) => (
        <div style={{ maxWidth: '600px', wordBreak: 'break-word' }}>
          <a
            onClick={() => navigate(`/assets/edit/${record.id}`)}
            style={{ color: '#1890ff', cursor: 'pointer' }}
          >
            {record.file_name ? record.file_name : '-'}
            <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '2px' }}>
              {record.file_url ? record.file_url : '-'}
            </div>
          </a>
        </div>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: '10%',
      sorter: true as any,
      render: (_: string, record: VideoType) => (
        <span style={{ color: '#1890ff', fontWeight: 500 }}>
          {record.duration ? formatDuration(record.duration) : '-'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (_: string, record: VideoType) => (
        <Tag color={getStatusColor(record.status || '')}>
          {record.status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      ),
    },
  ];

  const handleRemove = () => {
    console.log('Remove selected items:', selectedList);
  };

  const additionalButtons = [
    <Button key="remove" danger onClick={handleRemove} disabled={selectedList.length === 0}>
      Remove
    </Button>,
  ];

  return (
    <Component.BasePageComponent>
      {/* Filter Section */}
      <div
        style={{
          marginBottom: '16px',
          padding: '16px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          border: '1px solid #d9d9d9',
        }}
      >
        <Space wrap size="middle">
          <div>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Source</div>
            <Select
              style={{ width: 150 }}
              placeholder="All Sources"
              allowClear
              value={sourceFilter}
              onChange={handleSourceFilterChange}
              options={[
                { label: 'In-House', value: 'in-house' },
                { label: 'Mediahold', value: 'mediahold' },
                { label: 'KCP', value: 'kcp' },
              ]}
            />
          </div>
          <div>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
              Assigned to
            </div>
            <Select
              style={{ width: 150 }}
              placeholder="All"
              allowClear
              value={assignedFilter}
              onChange={handleAssignedFilterChange}
              options={[
                { label: 'Unassigned', value: 'false' },
                { label: 'Assigned', value: 'true' },
              ]}
            />
          </div>
          <div>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Status</div>
            <Select
              style={{ width: 150 }}
              placeholder="All Status"
              allowClear
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={[
                { label: 'COPYING', value: 'COPYING' },
                { label: 'TRANSCODING', value: 'TRANSCODING' },
                { label: 'COMPLETED', value: 'COMPLETED' },
                { label: 'FAILED', value: 'FAILED' },
              ]}
            />
          </div>
        </Space>
      </div>

      <Component.TableComponent<VideoType>
        name="Video Assets"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchAssets}
        columns={columns}
        additionalButton={additionalButtons}
        actions={{
          onSelectionChange: setSelectedList,
        }}
      />
    </Component.BasePageComponent>
  );
}
